import React, { ChangeEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BasePopup } from "components/pop-up/BasePopup";
import { useTooltip } from "context/TooltipProvider";
import {
  getChildUserOrgDetails,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { Referral } from "types";
import {} from "utils/helper";
import OrgUnitTreeSelect from "components/form/treeSelect";

interface ReferralForwardPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referral: Referral;
  onForwardSuccess?: (response: any) => void;
}

interface AdminUnit {
  uu_id: string;
  unit_name: string;
}

export const ReferralForwardPopup: React.FC<ReferralForwardPopupProps> = ({
  isOpen,
  onClose,
  referral,
  onForwardSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [adminUnits, setAdminUnits] = useState<AdminUnit[]>([]);
  const [selectedAdminUnit, setSelectedAdminUnit] = useState<string>("");
  const [forwardSummary, setForwardSummary] = useState<string>("");
  const [currentAdminUnitName, setCurrentAdminUnitName] = useState<string>("");
  const [currentAdminUnitID, setCurrentAdminUnitID] = useState<string>("");
  const [currentOrgUnitID, setCurrentOrgUnitID] = useState<string>("");
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && referral?.admin_unit_id) {
      fetchAdminUnits(referral.admin_unit_id, referral.org_unit_id);
    }
  }, [isOpen, referral]);

  const fetchAdminUnits = async (adminUnitId: string, orgUnitId?: string) => {
    try {
      const data = await getChildUserOrgDetails(adminUnitId, orgUnitId || "");
      setCurrentAdminUnitName(data?.admin_unit?.unit_name);
      setCurrentOrgUnitID(data?.org_unit?.uu_id || "");
      setCurrentAdminUnitID(data?.admin_unit?.uu_id || "");
      setAdminUnits(data.child_admin_units.units || []);
    } catch (error) {
      console.error("Error fetching admin units:", error);
      showTooltip(t("failed_to_load_destination_units"), "error");
    }
  };

  const handleAdminUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedAdminUnit(value);
    // setSelectedOrgUnit("");
  };

  const handleSummaryChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setForwardSummary(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAdminUnit) {
      showTooltip(
        t("please_select_destination_admin_unit", {
          defaultValue: "Please select a destination admin unit",
        }),
        "error",
      );
      return;
    }

    if (!forwardSummary.trim()) {
      showTooltip(
        t("please_enter_forwarding_decision_summary", {
          defaultValue: "Please enter forwarding decision summary",
        }),
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      // Append forwarding data
      formData.append("forwarding_decision_summary", forwardSummary);
      formData.append(
        "forwarded_from_admin_unit_name",
        currentAdminUnitName || "",
      );

      console.log({ selectedAdminUnit });
      if (selectedAdminUnit) {
        formData.append("admin_unit_id", selectedAdminUnit);
      }
      if (selectedUnit) {
        formData.append("org_unit_id", selectedUnit);
      }

      const response = await updateAssetReferral(referral?.id, formData);
      showTooltip(
        t("referral_forwarded_successfully", {
          defaultValue: "Referral forwarded successfully!",
        }),
        "success",
      );

      if (onForwardSuccess) {
        onForwardSuccess(response);
      }

      handleClose();
    } catch (error) {
      console.error("Error forwarding referral:", error);
      showTooltip(
        t("failed_to_forward_referral", {
          defaultValue: "Failed to forward referral",
        }),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAdminUnit("");
    setForwardSummary("");
    onClose();
    setSelectedUnit("");
  };

  const [selectedUnit, setSelectedUnit] = useState("");
  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Forward Referral"
      description="Forward this referral to another admin unit"
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col h-full">
          {/* Content area */}
          <div className="flex-1 space-y-4">
            {/* Destination Admin Unit */}
            <div className="flex flex-col">
              <label className="referral-popup-label">Admin Unit *</label>
              <select
                className="referral-form-field"
                value={selectedAdminUnit}
                onChange={handleAdminUnitChange}
                required
                disabled={loading}
              >
                <option value="">Select Admin Unit</option>
                {adminUnits?.map((unit) => (
                  <option key={unit.uu_id} value={unit.uu_id}>
                    {unit.unit_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              {" "}
              <label className="referral-popup-label">Org Unit *</label>
              <OrgUnitTreeSelect
                adminUnitId={currentAdminUnitID}
                orgUnitId={currentOrgUnitID}
                value={selectedUnit}
                onChange={setSelectedUnit}
                key={selectedUnit}
              />
            </div>

            {/* Forwarding Decision Summary */}
            <div className="flex flex-col">
              <label className="referral-popup-label">
                Forwarding Decision Summary *
              </label>
              <textarea
                className="referral-form-field h-24"
                value={forwardSummary}
                onChange={handleSummaryChange}
                placeholder="Provide details about why you are forwarding this referral..."
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Button container at bottom */}
          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedAdminUnit || !forwardSummary.trim()}
              className={`referral-form-button text-white px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                loading || !selectedAdminUnit || !forwardSummary.trim()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {loading ? "Forwarding..." : "Forward Referral"}
            </button>
          </div>
        </div>
      </form>
    </BasePopup>
  );
};

export default ReferralForwardPopup;
